/**
 * batch-68 handtekening (FO-345) - opgeslagen handtekening, ondertekenaars per
 * rol en volgorde, bevestigingsniveau en de foutcodes van het ondertekenen.
 *
 * De gedeelde bron voor API, portal en backoffice: de enums met labels, de
 * defaults per documentsoort, de foutcodes met hun melding (D1: elke weigering
 * toont de echte reden), de Zod-schema's van de mutaties (de API valideert
 * ermee, het formulier vooraf) en de DTO's. Alles hier is puur: geen DB, geen IO.
 *
 * Wat hier NIET staat: de handtekeningafbeelding zelf. Die gaat nooit door een
 * lijst-DTO; alleen de eigenaar krijgt een kortlevende voorbeeld-URL
 * (`GebruikerHandtekeningDTO.voorbeeldUrl`).
 */
import { z } from 'zod'

// ─── Enums ──────────────────────────────────────────────────────────────────

export const HANDTEKENING_SOORTEN = ['HANDTEKENING', 'PARAAF'] as const
export type HandtekeningSoort = (typeof HANDTEKENING_SOORTEN)[number]

export const HANDTEKENING_SOORT_LABELS: Record<HandtekeningSoort, string> = {
  HANDTEKENING: 'Handtekening',
  PARAAF: 'Paraaf',
}

/**
 * Rol = het vak waarin getekend wordt. MEDEWERKER, LEIDINGGEVENDE en HR zijn
 * dezelfde rollen als de gespreksverslag-ondertekenaars (spec 90); die lijst
 * hangt aan deze generieke lijst, de rollen bestaan maar één keer.
 */
export const ONDERTEKENAAR_ROLLEN = [
  'MEDEWERKER',
  'WERKGEVER',
  'GESPREKSVOERDER',
  'LEIDINGGEVENDE',
  'HR',
  'GETUIGE',
  'OVERIG',
] as const
export type OndertekenaarRol = (typeof ONDERTEKENAAR_ROLLEN)[number]

export const ONDERTEKENAAR_ROL_LABELS: Record<OndertekenaarRol, string> = {
  MEDEWERKER: 'Medewerker',
  WERKGEVER: 'Werkgever',
  GESPREKSVOERDER: 'Gespreksvoerder',
  LEIDINGGEVENDE: 'Leidinggevende',
  HR: 'HR',
  GETUIGE: 'Getuige',
  OVERIG: 'Overig',
}

/** De gespreksverslag-rollen (spec 90) zijn een deelverzameling: 1:1 dezelfde namen. */
export const GESPREKSVERSLAG_ROL_NAAR_ONDERTEKENAAR_ROL = {
  MEDEWERKER: 'MEDEWERKER',
  LEIDINGGEVENDE: 'LEIDINGGEVENDE',
  HR: 'HR',
} as const satisfies Record<'MEDEWERKER' | 'LEIDINGGEVENDE' | 'HR', OndertekenaarRol>

export const ONDERTEKEN_STATUSSEN = [
  'WACHT',
  'AAN_DE_BEURT',
  'GETEKEND',
  'VERVALLEN',
  'GEWEIGERD',
] as const
export type OndertekenStatus = (typeof ONDERTEKEN_STATUSSEN)[number]

export const ONDERTEKEN_STATUS_LABELS: Record<OndertekenStatus, string> = {
  WACHT: 'Wacht',
  AAN_DE_BEURT: 'Aan de beurt',
  GETEKEND: 'Getekend',
  VERVALLEN: 'Vervallen',
  GEWEIGERD: 'Geweigerd',
}

export const BEVESTIGINGS_NIVEAUS = ['STANDAARD', 'VERHOOGD'] as const
export type BevestigingsNiveau = (typeof BEVESTIGINGS_NIVEAUS)[number]

export const BEVESTIGINGS_NIVEAU_LABELS: Record<BevestigingsNiveau, string> = {
  STANDAARD: 'Standaard (lezen en klikken)',
  VERHOOGD: 'Verhoogd (opnieuw inloggen vlak voor het tekenen)',
}

// ─── Documentsoorten + defaults ─────────────────────────────────────────────

/** Soorten waarop de ondertekeninstelling (defaults + bevestigingsniveau) hangt. */
export const ONDERTEKEN_DOCUMENT_SOORTEN = [
  'GESPREKSVERSLAG',
  'ARBEIDSOVEREENKOMST',
  'GEHEIMHOUDING',
  'WERKGEVERSVERKLARING',
  'BEDRIJFSBREED',
  'OVERIG',
] as const
export type OndertekenDocumentSoort = (typeof ONDERTEKEN_DOCUMENT_SOORTEN)[number]

export const ONDERTEKEN_DOCUMENT_SOORT_LABELS: Record<OndertekenDocumentSoort, string> = {
  GESPREKSVERSLAG: 'Gespreksverslag',
  ARBEIDSOVEREENKOMST: 'Arbeidsovereenkomst, verlenging',
  GEHEIMHOUDING: 'Geheimhoudingsverklaring',
  WERKGEVERSVERKLARING: 'Werkgeversverklaring',
  BEDRIJFSBREED: 'Bedrijfsbreed beleid of verklaring ter kennisname',
  OVERIG: 'Overig (door HR aangemaakt)',
}

/** Eén standaard-ondertekenaar in een instelling: rol + volgorde, optioneel een vaste persoon. */
export const standaardOndertekenaarSchema = z.object({
  rol: z.enum(ONDERTEKENAAR_ROLLEN),
  volgorde: z.number().int().min(1).max(9),
  /** Vaste backoffice-ondertekenaar (bv. de standaard werkgever-ondertekenaar). */
  userId: z.string().min(1).nullable().optional(),
})
export type StandaardOndertekenaar = z.infer<typeof standaardOndertekenaarSchema>

export interface OndertekenSoortDefault {
  ondertekenaars: StandaardOndertekenaar[]
  bevestiging: BevestigingsNiveau
  gespreksvoerderTekentBijVrijgave: boolean
}

/**
 * Defaults uit FO-345 §3.4 zolang er geen instelling-rij is. Verhoogd staat aan
 * voor arbeidsovereenkomsten, werkgeversverklaringen en geheimhouding (§3.2).
 * Een WERKGEVER-regel zonder vaste `userId` wordt bij het aanmaken van de lijst
 * overgeslagen: zonder ingestelde standaard-ondertekenaar is er niemand om aan
 * te wijzen (HR kan hem per document toevoegen).
 */
export const ONDERTEKEN_SOORT_DEFAULTS: Record<OndertekenDocumentSoort, OndertekenSoortDefault> = {
  GESPREKSVERSLAG: {
    ondertekenaars: [
      { rol: 'GESPREKSVOERDER', volgorde: 1 },
      { rol: 'MEDEWERKER', volgorde: 2 },
    ],
    bevestiging: 'STANDAARD',
    gespreksvoerderTekentBijVrijgave: true,
  },
  ARBEIDSOVEREENKOMST: {
    ondertekenaars: [
      { rol: 'WERKGEVER', volgorde: 1 },
      { rol: 'MEDEWERKER', volgorde: 2 },
    ],
    bevestiging: 'VERHOOGD',
    gespreksvoerderTekentBijVrijgave: true,
  },
  GEHEIMHOUDING: {
    ondertekenaars: [
      { rol: 'WERKGEVER', volgorde: 1 },
      { rol: 'MEDEWERKER', volgorde: 2 },
    ],
    bevestiging: 'VERHOOGD',
    gespreksvoerderTekentBijVrijgave: true,
  },
  WERKGEVERSVERKLARING: {
    ondertekenaars: [{ rol: 'WERKGEVER', volgorde: 1 }],
    bevestiging: 'VERHOOGD',
    gespreksvoerderTekentBijVrijgave: true,
  },
  BEDRIJFSBREED: {
    ondertekenaars: [{ rol: 'MEDEWERKER', volgorde: 1 }],
    bevestiging: 'STANDAARD',
    gespreksvoerderTekentBijVrijgave: true,
  },
  OVERIG: {
    ondertekenaars: [{ rol: 'MEDEWERKER', volgorde: 1 }],
    bevestiging: 'STANDAARD',
    gespreksvoerderTekentBijVrijgave: true,
  },
}

// ─── Grenzen ────────────────────────────────────────────────────────────────

/** Opgeslagen afbeelding: maximaal 1000 x 400 px en 200 kB (FO-345 §3.1). */
export const HANDTEKENING_MAX_BREEDTE_PX = 1000
export const HANDTEKENING_MAX_HOOGTE_PX = 400
export const HANDTEKENING_MAX_BYTES = 200_000
/** Voorbeeld-URL en herauth-token leven hooguit vijf minuten. */
export const HANDTEKENING_VOORBEELD_TTL_SEC = 300
export const ONDERTEKEN_HERAUTH_TTL_SEC = 300
/** Ruwe invoer (canvas-export) mag groter zijn dan het opgeslagen resultaat. */
export const HANDTEKENING_INVOER_MAX_TEKENS = 2_000_000

export const HANDTEKENING_LEEG_MELDING = 'Het tekenvak is leeg - teken eerst je handtekening'

/**
 * Check van een getekende handtekening (client vóór het versturen, server na
 * het bijsnijden). Alleen een leeg vak wordt geweigerd: hoe iemand tekent is
 * aan de ondertekenaar, één doorgetrokken lijn is dus een geldige handtekening.
 * Een vak met alleen een stipje vangt de server af op het aantal inktpixels.
 * Geeft `null` als hij goed is, anders de melding.
 */
export function beoordeelHandtekeningKwaliteit(input: {
  soort: HandtekeningSoort
  aantalStreken: number
  /** Breedte van de inkt na bijsnijden (px). */
  breedtePx: number
}): string | null {
  return input.aantalStreken <= 0 ? HANDTEKENING_LEEG_MELDING : null
}

// ─── Verklaringen in het ondertekenvenster ─────────────────────────────────

export const ONDERTEKEN_VERKLARING_STANDAARD =
  'Door te ondertekenen bevestig je dat je dit document hebt gelezen en met de inhoud akkoord gaat.'

/** FO-332: tekenen is "voor gezien en akkoord"; reageren of bezwaar blijft mogelijk. */
export const ONDERTEKEN_VERKLARING_GESPREKSVERSLAG =
  'Met je handtekening bevestig je dat je het verslag hebt gelezen: voor gezien en akkoord. ' +
  'Ben je het ergens niet mee eens, dan kun je ook reageren of bezwaar maken. Dat kan ook nadat je hebt getekend.'

/** De gespreksvoerder tekent voor de juistheid van het verslag, niet "voor akkoord". */
export const ONDERTEKEN_VERKLARING_GESPREKSVOERDER =
  'Met je handtekening bevestig je als gespreksvoerder dat dit verslag het gesprek juist en volledig weergeeft.'

/** De verklaring in het ondertekenvenster voor deze documentsoort en dit vak. */
export function ondertekenVerklaring(soort: OndertekenDocumentSoort, rol: OndertekenaarRol | null): string {
  if (soort !== 'GESPREKSVERSLAG') return ONDERTEKEN_VERKLARING_STANDAARD
  return rol === 'GESPREKSVOERDER' ? ONDERTEKEN_VERKLARING_GESPREKSVOERDER : ONDERTEKEN_VERKLARING_GESPREKSVERSLAG
}

export const HANDTEKENING_HERINNERING_TEKST =
  'Zet je handtekening klaar, dan teken je documenten met één tik'

export const ONDERTEKENEN_OFFLINE_MELDING =
  'Je bent offline - tekenen kan zodra je weer verbinding hebt'

// ─── Foutcodes (D1) ─────────────────────────────────────────────────────────

/**
 * Elke weigering van het ondertekenen heeft een eigen code; de UI toont per
 * code de melding uit `ondertekenFoutMelding`. Een onbekende code toont nooit
 * een kale "mislukt" maar de code plus de referentie (correlatie-id).
 */
export const ONDERTEKEN_FOUTCODES = [
  'NOT_RELEASED',
  'NO_SIGNATURE_REQUIRED',
  'NOT_YOUR_TURN',
  'NOT_A_SIGNER',
  'VERSION_CHANGED',
  'ALREADY_SIGNED',
  'DEADLINE_PASSED',
  'REAUTH_REQUIRED',
  'HERAUTH_NIET_BESCHIKBAAR',
  'IMPERSONATION_NOT_ALLOWED',
  'NO_STORED_SIGNATURE',
  'HAS_SIGNATURES',
  'HR_BRIEF_GEWIJZIGD',
  // batch-70 uitrusting-controle (FO-531, T7): gereedschap tekenen alleen bij complete sets.
  'UITRUSTING_NIET_COMPLEET',
  // 2026-11-03: het vak heeft een plaats-stippellijn en de plaats ontbrak.
  'PLAATS_VEREIST',
  'VALIDATION_ERROR',
  'NOT_FOUND',
  'FORBIDDEN',
  'OFFLINE',
] as const
export type OndertekenFoutcode = (typeof ONDERTEKEN_FOUTCODES)[number]

export function isOndertekenFoutcode(code: string): code is OndertekenFoutcode {
  return (ONDERTEKEN_FOUTCODES as readonly string[]).includes(code)
}

/**
 * De melding bij een ondertekenfout (FO-345 §3.6). `rol` = wie eerst moet tekenen
 * (NOT_YOUR_TURN), `requestId` = de correlatie-id voor support.
 */
export function ondertekenFoutMelding(
  code: string,
  ctx: { rol?: string | null; requestId?: string | null; apiMelding?: string | null } = {},
): string {
  switch (code) {
    case 'NOT_RELEASED':
      return 'Dit document is nog niet vrijgegeven door HR.'
    case 'NO_SIGNATURE_REQUIRED':
      return 'Voor dit document is geen handtekening nodig.'
    case 'NOT_YOUR_TURN':
      return ctx.rol ? `Eerst moet ${ctx.rol} tekenen.` : 'Je bent nog niet aan de beurt om te tekenen.'
    case 'NOT_A_SIGNER':
      return 'Je bent niet aangewezen om dit document te tekenen.'
    case 'VERSION_CHANGED':
      return 'Het document is gewijzigd - bekijk de nieuwe versie en teken opnieuw.'
    case 'ALREADY_SIGNED':
      return 'Je hebt dit document al ondertekend.'
    case 'DEADLINE_PASSED':
      return 'De ondertekendeadline is verstreken.'
    case 'REAUTH_REQUIRED':
      return 'Voor dit document moet je eerst opnieuw inloggen. Daarna kun je tekenen.'
    case 'HERAUTH_NIET_BESCHIKBAAR':
      return `De verhoogde bevestiging is nu niet beschikbaar. Opnieuw inloggen helpt niet: neem contact op met de beheerder${
        ctx.requestId ? ` (referentie ${ctx.requestId})` : ''
      }.`
    case 'UITRUSTING_NIET_COMPLEET':
      return ctx.apiMelding ?? 'De gereedschapsovereenkomst teken je zodra je sets compleet zijn.'
    case 'IMPERSONATION_NOT_ALLOWED':
      return 'Tijdens "inloggen als" kun je niet tekenen. Een handtekening zet de medewerker altijd zelf.'
    case 'NO_STORED_SIGNATURE':
      return 'Je hebt nog geen opgeslagen handtekening. Teken hieronder je handtekening.'
    case 'HAS_SIGNATURES':
      return ctx.apiMelding ?? 'Er zijn al handtekeningen gezet; wijzigen maakt die ongeldig.'
    case 'HR_BRIEF_GEWIJZIGD':
      return ctx.apiMelding ?? 'Deze brief is gewijzigd na de controle en kan zo niet worden ondertekend.'
    case 'PLAATS_VEREIST':
      return 'Vul de plaats in waar je tekent. Die komt op het document te staan.'
    case 'VALIDATION_ERROR':
      return ctx.apiMelding ?? 'De handtekening kon niet worden gelezen. Teken opnieuw.'
    case 'NOT_FOUND':
      return 'Dit document bestaat niet (meer).'
    case 'FORBIDDEN':
      return ctx.apiMelding ?? 'Je hebt geen toegang tot dit document.'
    case 'OFFLINE':
      return ONDERTEKENEN_OFFLINE_MELDING
    default:
      return `Ondertekenen is niet gelukt (code ${code || 'ONBEKEND'}${
        ctx.requestId ? `, referentie ${ctx.requestId}` : ''
      }). Probeer het later opnieuw of neem contact op met HR.`
  }
}

/** De waarschuwing bij wijzigen na tekenen (409 HAS_SIGNATURES, FO-345 §3.4). */
export function heeftHandtekeningenWaarschuwing(aantal: number): string {
  return `Er ${aantal === 1 ? 'is al 1 handtekening' : `zijn al ${aantal} handtekeningen`}; wijzigen maakt die ongeldig. De ondertekenaars moeten daarna opnieuw tekenen.`
}

// ─── Schema's ───────────────────────────────────────────────────────────────

/** PNG als data-URL (canvas-export). Kale base64 accepteert alleen het legacy-veld. */
export const pngDataUrlSchema = z
  .string()
  .min(30, { message: 'De handtekening is leeg' })
  .max(HANDTEKENING_INVOER_MAX_TEKENS, { message: 'De handtekening is te groot' })
  .regex(/^data:image\/png;base64,[A-Za-z0-9+/=\s]+$/, {
    message: 'De handtekening moet een PNG-afbeelding zijn',
  })

/** PUT /me/handtekening - opslaan of vervangen. */
export const gebruikerHandtekeningOpslaanSchema = z.object({
  soort: z.enum(HANDTEKENING_SOORTEN),
  dataUrl: pngDataUrlSchema,
  /** Aantal streken op het tekenvak (0 = leeg). */
  aantalStreken: z.number().int().min(0).max(10_000),
})
export type GebruikerHandtekeningOpslaanInput = z.infer<typeof gebruikerHandtekeningOpslaanSchema>

/** De keuze in het ondertekenvenster: de opgeslagen handtekening of een nieuwe tekening. */
export const handtekeningKeuzeSchema = z.union([
  z.literal('OPGESLAGEN'),
  z.object({
    dataUrl: pngDataUrlSchema,
    aantalStreken: z.number().int().min(0).max(10_000).optional(),
  }),
])
export type HandtekeningKeuze = z.infer<typeof handtekeningKeuzeSchema>

/**
 * POST /documenten/:id/sign. De oude body `{ signatureData }` blijft één release
 * geaccepteerd (legacy-portal-builds); precies één van beide vormen.
 */
export const tekenDocumentSchema = z
  .object({
    handtekening: handtekeningKeuzeSchema.optional(),
    /** @deprecated FO-345 - één release geaccepteerd naast `handtekening`. */
    signatureData: z.string().min(50).max(500_000).optional(),
    /** Een nieuwe tekening ook opslaan als "mijn handtekening". */
    opslaanAlsStandaard: z.boolean().optional(),
    /** Teken in dit vak (alleen nodig als iemand meerdere vakken heeft). */
    rol: z.enum(ONDERTEKENAAR_ROLLEN).optional(),
    /** Verhoogde bevestiging: token uit POST /documenten/:id/herauth. */
    herauthToken: z.string().min(10).max(2000).optional(),
    /** De versie die de ondertekenaar heeft gelezen (VERSION_CHANGED bij een nieuwere). */
    documentVersionId: z.string().min(1).optional(),
    /** Plaats van ondertekening (optioneel, voor het vak "plaats"). */
    plaats: z.string().trim().max(80).optional(),
  })
  .refine((v) => (v.handtekening === undefined) !== (v.signatureData === undefined), {
    message: 'Kies je opgeslagen handtekening of teken een nieuwe',
    path: ['handtekening'],
  })
export type TekenDocumentInput = z.infer<typeof tekenDocumentSchema>

/**
 * Een tekstpunt op een geupload PDF (PDF-punten, oorsprong linksonder): het
 * begin van de basislijn waar de render een korte tekst zet.
 */
export const vakPuntSchema = z.object({
  pagina: z.number().int().min(0).max(999),
  x: z.number().min(0).max(5000),
  y: z.number().min(0).max(5000),
})
export type VakPunt = z.infer<typeof vakPuntSchema>

/**
 * Vakplaatsing op een geupload PDF, in PDF-punten (oorsprong linksonder).
 *
 * 2026-11-03 (verklaring uitsluitend zakelijk gebruik): naast het
 * handtekeningvak optioneel een `datum`- en `plaats`-punt. Een extern formulier
 * (bv. de afdruk van de Belastingdienst) heeft eigen stippellijnen "Plaats" en
 * "Datum"; de render vult daar de tekendatum en de opgegeven plaats in. Met een
 * plaats-punt vraagt het tekenvenster de plaats (verplicht).
 */
export const vakPlaatsingSchema = z.object({
  pagina: z.number().int().min(0).max(999),
  x: z.number().min(0).max(5000),
  y: z.number().min(0).max(5000),
  breedte: z.number().min(20).max(2000),
  hoogte: z.number().min(10).max(1000),
  datum: vakPuntSchema.nullable().optional(),
  plaats: vakPuntSchema.nullable().optional(),
})
export type VakPlaatsing = z.infer<typeof vakPlaatsingSchema>

/** Vraagt dit vak om een plaats bij het tekenen? (puur; API en UI delen dit) */
export function vakVraagtPlaats(vak: VakPlaatsing | null | undefined): boolean {
  return vak?.plaats != null
}

/** Eén ondertekenaar bij het (her)instellen van de lijst. */
export const ondertekenaarInvoerSchema = z
  .object({
    rol: z.enum(ONDERTEKENAAR_ROLLEN),
    userId: z.string().min(1).nullable().optional(),
    employeeId: z.string().min(1).nullable().optional(),
    volgorde: z.number().int().min(1).max(9),
    deadline: z.string().date().nullable().optional(),
    vakPlaatsing: vakPlaatsingSchema.nullable().optional(),
  })
  .refine((v) => Boolean(v.userId) !== Boolean(v.employeeId), {
    message: 'Kies precies één persoon: een medewerker of een gebruiker',
    path: ['employeeId'],
  })
export type OndertekenaarInvoer = z.infer<typeof ondertekenaarInvoerSchema>

/** PUT /documenten/:id/ondertekenaars - de lijst vervangen. */
export const ondertekenaarsBijwerkenSchema = z
  .object({
    ondertekenaars: z.array(ondertekenaarInvoerSchema).min(1, { message: 'Voeg minstens één ondertekenaar toe' }).max(20),
    /** Bij een document met al gezette handtekeningen: bevestigd dat die vervallen. */
    bevestigVervallen: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    // batch-68 (review I5) - één vak per persoon per rol.
    const gezien = new Set<string>()
    v.ondertekenaars.forEach((o, i) => {
      const persoon = o.employeeId ?? o.userId
      if (!persoon) return
      const sleutel = `${o.rol}:${persoon}`
      if (gezien.has(sleutel)) {
        ctx.addIssue({
          code: 'custom',
          path: ['ondertekenaars', i, o.employeeId ? 'employeeId' : 'userId'],
          message: 'Deze persoon staat al in deze rol',
        })
      }
      gezien.add(sleutel)
    })
  })
export type OndertekenaarsBijwerkenInput = z.infer<typeof ondertekenaarsBijwerkenSchema>

/** POST /documenten/:id/ondertekenaars/:oid/vervang - een andere ondertekenaar aanwijzen. */
export const ondertekenaarVervangSchema = z
  .object({
    userId: z.string().min(1).nullable().optional(),
    employeeId: z.string().min(1).nullable().optional(),
    reden: z.string().trim().min(3, { message: 'Geef een reden (minstens 3 tekens)' }).max(500),
    /**
     * batch-68 (review I2) - de deadline van de vervanger (yyyy-mm-dd, null =
     * geen). Weggelaten: hij erft de deadline, maar alleen als die nog loopt.
     */
    deadline: z.string().date().nullable().optional(),
  })
  .refine((v) => Boolean(v.userId) !== Boolean(v.employeeId), {
    message: 'Kies precies één persoon: een medewerker of een gebruiker',
    path: ['employeeId'],
  })
export type OndertekenaarVervangInput = z.infer<typeof ondertekenaarVervangSchema>

/**
 * PUT /documenten/:id/ondertekenaars/:oid/deadline - de deadline van één vak
 * verschuiven of weghalen (review I2: na een verstreken deadline kan het vak
 * anders nooit meer getekend worden).
 */
export const ondertekenaarDeadlineSchema = z.object({ deadline: z.string().date().nullable() })
export type OndertekenaarDeadlineInput = z.infer<typeof ondertekenaarDeadlineSchema>

/** PUT /documenten/:id/ondertekenaars/:oid/vak - het vak plaatsen (of wissen = null). */
export const ondertekenaarVakSchema = z.object({ vakPlaatsing: vakPlaatsingSchema.nullable() })

/** PUT /documenten/onderteken-instellingen/:soort */
export const ondertekenInstellingSchema = z.object({
  bevestiging: z.enum(BEVESTIGINGS_NIVEAUS),
  gespreksvoerderTekentBijVrijgave: z.boolean(),
  ondertekenaars: z
    .array(standaardOndertekenaarSchema)
    .min(1, { message: 'Voeg minstens één ondertekenaar toe' })
    .max(9, { message: 'Maximaal 9 ondertekenaars' }),
})
export type OndertekenInstellingInput = z.infer<typeof ondertekenInstellingSchema>

/**
 * POST /gespreksverslagen/:id/ondertekenen - "Onderteken als gespreksvoerder".
 * Leeg = de bestaande STREEP-registratie zonder afbeelding (iemand anders dan de
 * gespreksvoerder rondt de stap af; diens vak blijft dan "nog te tekenen").
 */
export const gespreksvoerderOndertekenSchema = z.object({
  handtekening: handtekeningKeuzeSchema.optional(),
  opslaanAlsStandaard: z.boolean().optional(),
  herauthToken: z.string().min(10).max(2000).optional(),
})
export type GespreksvoerderOndertekenInput = z.infer<typeof gespreksvoerderOndertekenSchema>

/** Bevestiging bij een inhoudelijke wijziging ná een handtekening (409 HAS_SIGNATURES). */
export const bevestigVervallenSchema = z.object({ bevestigVervallen: z.boolean().optional() })

// ─── DTO's ──────────────────────────────────────────────────────────────────

/** Eigen opgeslagen handtekening of paraaf (alleen naar de eigenaar). */
export interface GebruikerHandtekeningDTO {
  id: string
  soort: HandtekeningSoort
  opgeslagenOp: string
  breedtePx: number
  hoogtePx: number
  /**
   * Relatief same-origin pad met een HMAC-token, geldig tot `voorbeeldVerlooptOp`.
   * null tijdens "inloggen als": dan ziet ook de beheerder de afbeelding niet.
   */
  voorbeeldUrl: string | null
  voorbeeldVerlooptOp: string | null
}

export interface MijnHandtekeningenDTO {
  handtekening: GebruikerHandtekeningDTO | null
  paraaf: GebruikerHandtekeningDTO | null
  /** Tijdens "inloggen als": alleen bekijken, niet wijzigen. */
  alleenLezen: boolean
}

/**
 * Eén ondertekenaar. batch-68 (integratie, K5): buiten de beheerweergave (GET
 * /documenten/:id/ondertekenaars) zijn `userId`, `employeeId`,
 * `opgeslagenGebruikt` en `laatsteHerinneringOp` van ANDERE ondertekenaars
 * altijd null; van het eigen vak (`isIkZelf`) zijn ze gevuld.
 */
export interface DocumentOndertekenaarDTO {
  id: string
  versionNr: number
  rol: OndertekenaarRol
  rolLabel: string
  naam: string
  /** Alleen in de beheerweergave of voor het eigen vak; anders null. */
  userId: string | null
  /** Alleen in de beheerweergave of voor het eigen vak; anders null. */
  employeeId: string | null
  volgorde: number
  verplicht: boolean
  status: OndertekenStatus
  statusLabel: string
  deadline: string | null
  aanDeBeurtSinds: string | null
  getekendOp: string | null
  /**
   * Getekend met de opgeslagen handtekening (bewijs; geen afbeelding). Alleen in
   * de beheerweergave of voor het eigen vak; anders null.
   */
  opgeslagenGebruikt: boolean | null
  bevestiging: BevestigingsNiveau | null
  vakPlaatsing: VakPlaatsing | null
  vervangenOp: string | null
  vervangReden: string | null
  /** Alleen in de beheerweergave of voor het eigen vak; anders null. */
  laatsteHerinneringOp: string | null
  /** Is dit de ingelogde gebruiker? */
  isIkZelf: boolean
}

/** Een vervallen handtekening in de historie (na een inhoudswijziging). */
export interface VervallenHandtekeningDTO {
  signatureId: string
  rol: OndertekenaarRol
  naam: string
  versionNr: number | null
  getekendOp: string
  vervallenOp: string
  reden: string | null
}

/** GET /documenten/:id/ondertekening - alles wat het ondertekenvenster nodig heeft. */
export interface DocumentOndertekeningDTO {
  documentId: string
  titel: string
  soort: OndertekenDocumentSoort
  isGespreksverslag: boolean
  /** De CONTENT-versie waarop getekend wordt (bron van de hash). */
  versie: { id: string; versionNr: number; fileName: string; sha256: string | null } | null
  /** De versie die iedereen ziet (getekend, of de bron als er nog niets getekend is). */
  weergaveVersie: { versionNr: number; fileName: string } | null
  bevestiging: BevestigingsNiveau
  verklaring: string
  ondertekenaars: DocumentOndertekenaarDTO[]
  /** Mijn vak dat nu aan de beurt is (null = niets te tekenen voor mij). */
  mijnBeurt: {
    ondertekenaarId: string | null
    rol: OndertekenaarRol
    rolLabel: string
    /**
     * 2026-11-03: het vak heeft een plaats-stippellijn; het tekenvenster vraagt
     * de plaats (verplicht). `voorstel` = de woonplaats uit het dossier.
     */
    plaats: { voorstel: string | null } | null
  } | null
  /** Als ik nog niet aan de beurt ben: wie eerst moet tekenen. */
  wachtOp: string | null
  heeftOpgeslagenHandtekening: boolean
  /** Status voor de bibliotheek: Getekend (alle vakken) of Deels getekend (x/y). */
  statusTekst: string
  aantalGetekend: number
  /** Alle actieve vakken (ook niet-verplichte): de noemer van "Deels getekend (x/y)". */
  aantalVakken: number
  vervallen: VervallenHandtekeningDTO[]
  /** Legacy: document zonder lijst (eigen document of bedrijfsbreed). */
  zonderLijst: boolean
}

export interface TeOndertekenenItemDTO {
  documentId: string
  titel: string
  rol: OndertekenaarRol
  rolLabel: string
  deadline: string | null
  aanDeBeurtSinds: string | null
  isGespreksverslag: boolean
  /** Eerder getekend maar vervallen door een inhoudswijziging. */
  opnieuwTekenen: boolean
}

export interface TeOndertekenenDTO {
  count: number
  items: TeOndertekenenItemDTO[]
}

export interface OndertekenInstellingDTO {
  soort: OndertekenDocumentSoort
  soortLabel: string
  bevestiging: BevestigingsNiveau
  gespreksvoerderTekentBijVrijgave: boolean
  ondertekenaars: Array<StandaardOndertekenaar & { naam: string | null }>
  /** True = er is (nog) geen rij: dit zijn de defaults uit code. */
  isDefault: boolean
  gewijzigdOp: string | null
}

export interface HerauthTokenDTO {
  herauthToken: string
  verlooptOp: string
}

/** Resultaat van een geslaagde ondertekening. */
export interface TekenResultaatDTO {
  id: string
  documentId: string
  employeeId: string | null
  userId: string | null
  employeeName: string
  rol: OndertekenaarRol
  signedAt: string
  /** De nieuwe getekende versie (null = render nog niet gelukt; opnieuw-actie staat klaar). */
  getekendeVersieNr: number | null
  /** Alle verplichte vakken getekend? */
  alleGetekend: boolean
  /**
   * batch-68 (review I11) - staat er nog een leeg vak (ook een niet-verplicht)?
   * Alleen dan hoort de statustekst achter "Getekend op ...".
   */
  nogTeTekenen: boolean
  statusTekst: string
}

/** Statustekst voor de bibliotheek en het dossier: `totaal` = alle actieve vakken. */
export function ondertekenStatusTekst(getekend: number, totaal: number): string {
  if (totaal <= 0) return 'Geen ondertekening nodig'
  if (getekend >= totaal) return 'Getekend'
  if (getekend === 0) return 'Nog niet getekend'
  return `Deels getekend (${getekend}/${totaal})`
}
